import { useState, useEffect } from 'react';
import { FeatureFlags } from '../utils/featureFlags';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'useFeatureFlag' });

interface UseFeatureFlagResult {
  isEnabled: boolean;
  isLoading: boolean;
  error: Error | null;
}

export function useFeatureFlag(flagName: string): UseFeatureFlagResult {
  // In development mode, start with true to avoid flashing
  // In production, start with false for security
  const initialValue = import.meta.env.DEV ? true : false;
  
  const [isEnabled, setIsEnabled] = useState<boolean>(initialValue);
  const [isLoading, setIsLoading] = useState<boolean>(!import.meta.env.DEV); // Don't show loading in dev
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function checkFlag() {
      try {
        // Special case: if flagName is 'dummy', immediately return true without API call
        if (flagName === 'dummy') {
          logger.debug(`Using default true for dummy feature flag`);
          if (isMounted) {
            setIsEnabled(true);
            setIsLoading(false);
          }
          return;
        }
        
        // In development mode, skip API call and use default true
        if (import.meta.env.DEV) {
          logger.debug(`In development mode, using default true for feature flag: ${flagName}`);
          if (isMounted) {
            setIsEnabled(true);
            setIsLoading(false);
          }
          return;
        }
        
        const enabled = await FeatureFlags.isEnabled(flagName);
        if (isMounted) {
          setIsEnabled(enabled);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Failed to check feature flag'));
          setIsLoading(false);
        }
      }
    }

    checkFlag();

    return () => {
      isMounted = false;
    };
  }, [flagName]);

  return { isEnabled, isLoading, error };
} 